import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import {
  LinkList,
  LinkDetail,
  AddLinkFAB,
  AddButton,
  AppHeader,
  FiltersSection,
  MainContent,
  type LinkItemData,
  type FilterPeriod,
} from '../components';
import { useAuth, useAppModals, useLinkActions, useTheme, useDrawer, useMediaQuery } from '../hooks';
import { apiWithRetry } from '../lib/api-interceptor';
import { useAuthStore } from '../stores/authStore';
import { listContextGroups } from '../api/services';

const transformLink = (link: Record<string, unknown>): LinkItemData => ({
  id: (link.id as string) || '',
  url: (link.url as string) || '',
  title: (link.title as string) ?? null,
  customTitle: (link.customTitle as string) ?? null,
  tags: (link.tags as string[]) ?? [],
  description: (link.description as string) ?? null,
  image: (link.image as string) ?? null,
  isFavorite: (link.isFavorite as boolean) || false,
  createdAt: (link.createdAt as string) || new Date().toISOString(),
});

export const GroupDetailScreen = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [search, setSearch] = useState('');
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all');
  const [selectedLink, setSelectedLink] = useState<LinkItemData | null>(null);

  useTheme();
  const { isAuthenticated, isInitialized, email, fullName, avatarUrl } = useAuth();
  const {
    openAddLinkModal,
    closeAddLinkModal,
    openAuthPrompt,
  } = useAppModals();
  const { openDrawer } = useDrawer();

  const groupsQuery = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const res = await listContextGroups();
      return res.data ?? [];
    },
  });

  const group = groupsQuery.data?.find((g) => g.id === id);

  const fetchLinks = async () => {
    const anonymousId = useAuthStore.getState().anonymousId;
    const params: Record<string, string> = { contextGroupId: id! };
    if (filterPeriod !== 'all') params.period = filterPeriod;
    params.timezoneOffset = String(new Date().getTimezoneOffset());
    if (anonymousId) params.anonymousId = anonymousId;

    const res = await apiWithRetry<{ success: boolean; data: unknown[] }>({
      url: '/list-links',
      method: 'GET',
      params,
    });
    return res;
  };

  const searchLinks = async () => {
    const anonymousId = useAuthStore.getState().anonymousId;
    const params: Record<string, string> = { q: search, contextGroupId: id! };
    if (filterPeriod !== 'all') params.period = filterPeriod;
    params.timezoneOffset = String(new Date().getTimezoneOffset());
    if (anonymousId) params.anonymousId = anonymousId;

    const res = await apiWithRetry<{ success: boolean; data: unknown[] }>({
      url: '/search-links',
      method: 'GET',
      params,
    });
    return res;
  };

  const isSearching = search.length >= 2;

  const linksQuery = useQuery({
    queryKey: ['links', 'group', id, filterPeriod],
    queryFn: fetchLinks,
    enabled: !!id && !isSearching && isInitialized,
  });

  const searchQuery = useQuery({
    queryKey: ['links', 'search', search, id, filterPeriod],
    queryFn: searchLinks,
    enabled: !!id && isSearching && isInitialized,
  });

  const rawLinks = (isSearching ? searchQuery.data?.data : linksQuery.data?.data ?? []) as Record<string, unknown>[];
  const links: LinkItemData[] = rawLinks.map(transformLink);
  const { deleteLinkMutation, updateLinkMutation, handleFavoriteToggle, handleDeleteLink, handleUpdateLink } = useLinkActions({
    onLinkCreated: () => closeAddLinkModal(),
    onLinkDeleted: () => setSelectedLink(null),
  });

  const handleUpdateLinkWithLocalState = async (
    linkId: string,
    data: { customTitle?: string | null; tags?: string[] }
  ) => {
    if (selectedLink && selectedLink.id === linkId) {
      setSelectedLink({
        ...selectedLink,
        ...(data.customTitle !== undefined && { customTitle: data.customTitle }),
        ...(data.tags !== undefined && { tags: data.tags }),
      });
    }
    await handleUpdateLink(linkId, data);
  };

  const handleFavoriteToggleWithLocalState = (link: LinkItemData) => {
    if (selectedLink && selectedLink.id === link.id) {
      setSelectedLink({ ...selectedLink, isFavorite: !selectedLink.isFavorite });
    }
    handleFavoriteToggle(link);
  };

  const handleAuthClick = () => {
    if (isAuthenticated) openDrawer();
    else openAuthPrompt();
  };

  if (!id) return null;
  if (!group && !groupsQuery.isLoading) {
    return (
      <div style={{ padding: 'var(--space-lg)' }}>
        <button type="button" onClick={() => navigate('/groups')} style={{ color: 'var(--color-accent)', marginBottom: 16 }}>
          <ArrowLeft size={20} /> Back
        </button>
        <p>Group not found</p>
      </div>
    );
  }

  return (
    <>
      <AppHeader
        isAuthenticated={isAuthenticated}
        fullName={fullName}
        email={email}
        avatarUrl={avatarUrl}
        onAuthClick={handleAuthClick}
        hideAuthOnDesktop={isDesktop}
      />
      <div style={{ padding: 'var(--space-md)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
          <div>
            <button
              type="button"
              onClick={() => navigate('/groups')}
              style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-accent)', marginBottom: 8, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <ArrowLeft size={20} /> {t('common.back', 'Back')}
            </button>
            <h1 style={{ fontSize: 'var(--font-xl)', color: 'var(--color-text-primary)' }}>
              {group?.name ?? '...'}
            </h1>
          </div>
          {isDesktop && <AddButton onClick={() => openAddLinkModal(undefined, id)} label={t('addLink.title', 'Add link')} />}
        </div>
      </div>
      <FiltersSection
        search={search}
        filterPeriod={filterPeriod}
        selectedGroupId={null}
        groups={[]}
        onSearchChange={(e) => setSearch(e.target.value)}
        onSearchClear={() => setSearch('')}
        onFilterChange={setFilterPeriod}
        onGroupChange={() => {}}
      />
      <MainContent>
        <LinkList
          links={links}
          isLoading={linksQuery.isLoading}
          isRefetching={linksQuery.isFetching}
          error={linksQuery.error ? String(linksQuery.error) : null}
          onLinkClick={(l) => window.open(l.url, '_blank')}
          onFavoriteToggle={handleFavoriteToggleWithLocalState}
          onMenuClick={setSelectedLink}
          emptyTitle={t('links.noLinksYet')}
          emptyDescription={t('links.tapToSaveFirst')}
        />
      </MainContent>
      {!isDesktop && <AddLinkFAB onClick={() => openAddLinkModal(undefined, id)} />}
      {selectedLink && (
        <LinkDetail
          link={selectedLink}
          onClose={() => setSelectedLink(null)}
          onOpenLink={() => window.open(selectedLink.url, '_blank')}
          onDelete={() => handleDeleteLink(selectedLink.id)}
          onFavoriteToggle={() => handleFavoriteToggleWithLocalState(selectedLink)}
          onUpdate={handleUpdateLinkWithLocalState}
          isDeleting={deleteLinkMutation.isPending}
          isUpdating={updateLinkMutation.isPending}
        />
      )}
    </>
  );
};
